/* eslint-disable no-console */
import { retry } from '../../components/utils/retry';
import { StoryblokService } from '../storyblok';
import { StringService } from '../string';

export interface Query {
  query: string;
}

export interface TeaserItem<Content> {
  content: Content;
  default_full_slug?: string;
  full_slug: string;
  id: number;
  published_at: string;
}

interface DefaultTeaserContent {
  teaser_title?: string;
  teaser_text?: string;
  teaser_image?: {
    filename: string;
  };
  teaser_type?: string;
}

type ValidContentTypes = 'Page' | 'Pagepressnews' | 'Aktuelles' | 'Didarticlepage' | 'PageProduct';

const contentTypes: ValidContentTypes[] = ['Page', 'Pagepressnews', 'Aktuelles', 'Didarticlepage', 'PageProduct'];

export type ContentTypeItem = TeaserItem<DefaultTeaserContent>;

interface TeaserQueryResult {
  data: {
    PageItem?: ContentTypeItem | null;
  };
}

export const TeaserService = {
  settings: {
    ...StoryblokService.getConfig(),
  },

  async getTeaserContentType(teaserId: number): Promise<ValidContentTypes | null> {
    const fetchTeaserContentType: () => Promise<ValidContentTypes | null> = async () => {
      try {
        const response = await fetch(
          this.settings.options.url,
          this.createRequest(this.fetchContentNode(teaserId)),
        );

        if (!response.ok) {
          console.error(`HTTP error! status: ${response.status}`);
          return null;
        }

        const result = await response.json();
        // eslint-disable-next-line max-len
        const contentType = StringService.kebabToCapitalize(result.data.ContentNode.content.component) || null;
        if (contentType && contentTypes.includes(contentType as ValidContentTypes)) {
          return contentType as ValidContentTypes;
        }

        console.error(`Invalid content type: ${contentType}`);
        return null;
      } catch (error) {
        console.error('Error fetching teaser: ', error);
        return null;
      }
    };

    return retry(fetchTeaserContentType);
  },

  // eslint-disable-next-line max-len
  async getTeaser(teaserId: number, contentType: ValidContentTypes): Promise<ContentTypeItem | null> {
    const fetchTeaser: () => Promise<ContentTypeItem> = async () => {
      try {
        const response = await fetch(
          this.settings.options.url,
          this.createRequest(this.createTeaserByIdQuery(teaserId, contentType)),
        );

        if (!response.ok) {
          console.error(`HTTP error! status: ${response.status}`);
          return null;
        }

        const result = await response.json();
        return result.data[`${contentType}Item`] || null;
      } catch (error) {
        console.error('Error fetching teaser: ', error);
        return null;
      }
    };

    return retry(fetchTeaser);
  },

  parseQueryResults(queryResult: TeaserQueryResult): ContentTypeItem {
    const {
      PageItem,
    } = queryResult.data;
    return {
      ...(PageItem || null),
    };
  },

  createRequestHeaders(): HeadersInit {
    const { options: { accessToken } } = this.settings;

    return {
      token: accessToken as string,
      version: 'published',
      Accept: 'application/json',
      // eslint-disable-next-line @typescript-eslint/naming-convention
      'Content-Type': 'application/json',
    };
  },

  createRequest(query: { query: string }): RequestInit {
    return {
      method: 'POST',
      mode: 'cors',
      headers: this.createRequestHeaders(),
      body: JSON.stringify(query),
    };
  },

  fetchContentNode(teaserId: number): Query {
    const parameters = `id: ${teaserId}, resolve_links: "url"`;
    return {
      query: `{
        ContentNode(${parameters}) {
          content
        }
      }`,
    };
  },

  createTeaserByIdQuery(teaserId: number, contentType: ValidContentTypes): Query {
    const parameters = `id: ${teaserId}`;
    return {
      query: `{
        ${contentType}Item(${parameters}) {
          full_slug
          content {
            teaser_title
            teaser_text
            teaser_type
            teaser_image {
              filename
            }
          }
          published_at
        }
      }`,
    };
  },
};
